import type { RSVP, TicketType } from '@/types';

/**
 * Parse comma-separated ticket IDs from an RSVP ticketFilter field.
 */
export function parseCommaSeparatedTicketIds(ticketFilter?: string | null): string[] {
    if (!ticketFilter?.trim()) {
        return [];
    }

    return ticketFilter.split(',').map((id) => id.trim()).filter(Boolean);
}

/**
 * Collect ticket type IDs referenced in responses.Tickets (RSVP snapshot lines).
 */
export function getTicketIdsUsedInResponses(responses?: Record<string, unknown> | null): string[] {
    if (!responses || !Object.prototype.hasOwnProperty.call(responses, 'Tickets')) {
        return [];
    }

    const arr = responses.Tickets;
    if (!Array.isArray(arr)) {
        return [];
    }

    const ids: string[] = [];
    for (const entry of arr) {
        if (entry && typeof entry === 'object' && typeof entry.id === 'string' && entry.id.trim()) {
            ids.push(entry.id.trim());
        }
    }

    return ids;
}

/**
 * Tickets the inviter may use when inviting others or assigning tickets to their guests.
 * Includes all unrestricted tickets, plus restricted tickets the inviter has used
 * or that appear in their ticketFilter (assigned scope).
 */
export function getInviterAccessibleTickets(
    eventTickets: TicketType[] | undefined,
    inviterRsvp: RSVP,
): TicketType[] {
    if (!eventTickets?.length) {
        return [];
    }

    const usedIds = new Set(getTicketIdsUsedInResponses(inviterRsvp.responses));
    const filterIds = new Set(parseCommaSeparatedTicketIds(inviterRsvp.ticketFilter));
    const allowedRestrictedIds = new Set<string>([...usedIds, ...filterIds]);

    return eventTickets.filter((t) => {
        if (!t.restricted) {
            return true;
        }

        return allowedRestrictedIds.has(t.id);
    });
}

/**
 * When an inviter RSVP cannot be loaded, only unrestricted tickets are safe to show.
 */
export function getInviterAccessibleTicketsWithFallback(
    eventTickets: TicketType[] | undefined,
    inviterRsvp: RSVP | null,
): TicketType[] {
    if (!eventTickets?.length) {
        return [];
    }

    if (!inviterRsvp) {
        return eventTickets.filter((t) => !t.restricted);
    }

    return getInviterAccessibleTickets(eventTickets, inviterRsvp);
}

/**
 * Limit to tickets whose IDs appear in allowList (e.g. URL tickets param). Preserves event order.
 */
export function filterEventTicketsByIdAllowList(
    tickets: TicketType[],
    allowList: string[],
): TicketType[] {
    if (!allowList.length) {
        return tickets;
    }

    const set = new Set(allowList);

    return tickets.filter((t) => set.has(t.id));
}
